import { useContext, useState } from 'react';
import { View as RNView, StyleProp, ViewStyle } from 'react-native';
import { ActionSheet } from '@shared/components/ActionSheet';
import { View } from '@shared/components/View';
import { MBCardChannelProvider } from '@shared/components/blocks/contexts/channel';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useNavigator } from '@shared/util/navigation';
import { useCard } from '../../hooks';
import { HiddenCardOptions, useOpenCardOptionsMenu } from '../useOpenCardOptionsMenu';
import { CardExport } from './CardExport';
import { ShareSheet } from './ShareSheet';

interface Props {
  cardId: string;
  channelSlug: string;
  hiddenCardOptions?: HiddenCardOptions;
  style?: StyleProp<ViewStyle>;
}

export function MBCardDetailsOptionsButton({ cardId, channelSlug, ...props }: Props) {
  const { card } = useCard(cardId);
  return (
    <MBCardChannelProvider channelSlug={channelSlug}>
      {card && <CardDetailsOptionsButton {...{ card, ...props }} />}
    </MBCardChannelProvider>
  );
}

interface CardDetailsOptionsButtonProps extends Pick<Props, 'style' | 'hiddenCardOptions'> {
  card: API.CardFragment;
}

export function CardDetailsOptionsButton({
  card,
  style,
  hiddenCardOptions,
}: CardDetailsOptionsButtonProps) {
  const colors = useContext(ColorThemeContext);
  const { goBack } = useNavigator();
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [showDownloadSheet, setShowDownloadSheet] = useState(false);
  const anchorName = getAnchorName('card-details-options', card.id);

  const { isMenuOpen, openMenu, closeMenu, menuOptions, removeMenuState, bulkEditMenuState } =
    useOpenCardOptionsMenu(card, {
      onRemoved: goBack,
      hiddenCardOptions,
      onShare: () => setShowShareSheet(true),
      onDownload: () => setShowDownloadSheet(true),
    });

  return (
    <View>
      <MBTouchableOpacity onPress={openMenu}>
        <View style={{ anchorName }}>
          <RNView style={style}>
            <MBIconV2 color={colors.foregroundActive} name="dots" />
          </RNView>
        </View>
      </MBTouchableOpacity>
      {isMenuOpen && (
        <ActionSheet
          anchorName={anchorName}
          align="right"
          onClose={closeMenu}
          options={menuOptions}
        />
      )}
      {removeMenuState.isOpen && (
        <ActionSheet
          anchorName={anchorName}
          align="right"
          title={removeMenuState.removeMenuTitle}
          onClose={removeMenuState.closeRemoveMenu}
          options={removeMenuState.removeMenuOptions}
        />
      )}
      {bulkEditMenuState.isOpen && (
        <ActionSheet
          anchorName={anchorName}
          align="right"
          title={bulkEditMenuState.bulkEditMenuTitle}
          onClose={bulkEditMenuState.closeBulkEditMenu}
          options={bulkEditMenuState.bulkEditMenuOptions}
          style={{ maxWidth: grid(32) }}
        />
      )}
      {showShareSheet && (
        <ShareSheet
          anchorName={anchorName}
          card={{ id: card.id, workflowTemplateId: card.workflowTemplateId ?? null }}
          onCloseSheet={() => setShowShareSheet(false)}
        />
      )}
      {showDownloadSheet && (
        <CardExport
          anchorName={anchorName}
          card={{ id: card.id, workflowTemplateId: card.workflowTemplateId ?? null }}
          onCloseSheet={() => setShowDownloadSheet(false)}
        />
      )}
    </View>
  );
}
